import { useEffect, useRef } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  Radio,
  Box,
} from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { useForm } from "@mantine/form";
import { IconPhone, IconMail } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useAddFollowUp } from "../../hooks/useSendOuts";
import {
  scrollToFirstError,
  showValidationNotification,
} from "../../utils/formValidation";

interface LogFollowUpModalProps {
  opened: boolean;
  onClose: () => void;
  sendOutId: string;
  defaultType?: "call" | "email";
  defaultContactPerson?: string;
  onSuccess?: () => void;
}

interface FollowUpFormValues {
  type: "call" | "email";
  contactPerson: string;
  subject: string;
}

export default function LogFollowUpModal({
  opened,
  onClose,
  sendOutId,
  defaultType = "call",
  defaultContactPerson = "",
  onSuccess,
}: LogFollowUpModalProps) {
  const addFollowUpMutation = useAddFollowUp();
  const isUpdatingEditorRef = useRef(false);

  const form = useForm<FollowUpFormValues>({
    initialValues: {
      type: defaultType,
      contactPerson: "",
      subject: "",
    },
  });

  const notesEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder:
          "Describe the call - who you spoke with, outcome, next steps...",
      }),
      Underline,
    ],
    content: "",
  });

  // Reset form and editor when modal opens
  useEffect(() => {
    if (opened) {
      form.reset();
      form.setFieldValue("type", defaultType);
      form.setFieldValue("contactPerson", defaultContactPerson);
      if (notesEditor) {
        isUpdatingEditorRef.current = true;
        notesEditor.commands.setContent("");
        isUpdatingEditorRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, defaultType, defaultContactPerson]);

  // Update placeholder based on type
  useEffect(() => {
    if (!notesEditor) return;
    const placeholder =
      form.values.type === "call"
        ? "Describe the call - who you spoke with, outcome, next steps..."
        : "Describe the email content and any response received...";
    notesEditor.extensionManager.extensions
      .find((e) => e.name === "placeholder")
      ?.configure({ placeholder });
  }, [form.values.type, notesEditor]);

  const handleSubmit = async (values: FollowUpFormValues) => {
    const notesHtml = notesEditor?.getHTML() || "";
    const notesText = notesHtml.replace(/<[^>]*>/g, "").trim();

    if (!notesText) {
      notifications.show({
        title: "Validation Error",
        message: "Notes are required",
        color: "red",
      });
      return;
    }

    try {
      await addFollowUpMutation.mutateAsync({
        id: sendOutId,
        data: {
          type: values.type,
          contactPerson: values.contactPerson || undefined,
          subject:
            values.type === "email" ? values.subject || undefined : undefined,
          notes: notesHtml,
        },
      });

      notifications.show({
        title: "Follow-Up Logged",
        message: `${
          values.type === "call" ? "Call" : "Email"
        } follow-up recorded`,
        color: "green",
      });

      form.reset();
      onSuccess?.();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to log follow-up";
      showValidationNotification(message);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Log Follow-Up</Text>}
      size="lg"
      centered
      closeOnClickOutside={false}
    >
      <form
        noValidate
        onSubmit={form.onSubmit(handleSubmit, () => scrollToFirstError(form))}
      >
        <Stack gap="md">
          {/* Type Selection */}
          <Radio.Group
            label="Type"
            value={form.values.type}
            onChange={(val) =>
              form.setFieldValue("type", val as "call" | "email")
            }
          >
            <Group mt="xs">
              <Radio
                value="call"
                label={
                  <Group gap="xs">
                    <IconPhone size={14} />
                    <Text size="sm">Phone Call</Text>
                  </Group>
                }
              />
              <Radio
                value="email"
                label={
                  <Group gap="xs">
                    <IconMail size={14} />
                    <Text size="sm">Email</Text>
                  </Group>
                }
              />
            </Group>
          </Radio.Group>

          {/* Contact Person / Recipient */}
          <TextInput
            label={form.values.type === "call" ? "Contact Person" : "Recipient"}
            placeholder={
              form.values.type === "call"
                ? "Name of person contacted (optional)"
                : "Email recipient name (optional)"
            }
            {...form.getInputProps("contactPerson")}
          />

          {/* Subject - only for emails */}
          {form.values.type === "email" && (
            <TextInput
              label="Subject"
              placeholder="Email subject line (optional)"
              {...form.getInputProps("subject")}
            />
          )}

          {/* Notes - Rich Text */}
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Notes{" "}
              <span style={{ color: "var(--mantine-color-red-6)" }}>*</span>
            </Text>
            <RichTextEditor editor={notesEditor}>
              <RichTextEditor.Toolbar>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Bold />
                  <RichTextEditor.Italic />
                  <RichTextEditor.Underline />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.BulletList />
                  <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>
              <RichTextEditor.Content style={{ minHeight: 160 }} />
            </RichTextEditor>
          </Box>

          {/* Actions */}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color={form.values.type === "call" ? "blue" : "grape"}
              loading={addFollowUpMutation.isPending}
            >
              Log Follow-Up
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
